import { z } from 'zod';

/**
 * The small Otto of the header (PLAN-BUREAU lot 4, asked on 07/10/2026): what Otto does NOW on his
 * machine — a mission an execution moves, the workshop driving a browser, a loop running —, what
 * comes next, and where he got stuck, read from `get_otto_now()` and said in plain words, nothing
 * technical. Pure: the answer is validated here, the words are chosen here, the component paints.
 */

const doingSchema = z.object({
  kind: z.enum(['mission', 'workshop', 'loop']),
  action: z.string().nullable(),
  thread_id: z.string().nullable(),
  title: z.string().nullable(),
  client: z.string().nullable(),
  since: z.string().nullable(),
  control: z.string().nullable().optional(),
});

const nextSchema = z.object({
  at: z.string(),
  action: z.string().nullable(),
  thread_id: z.string(),
  title: z.string().nullable(),
  client: z.string().nullable(),
});

export const ottoNowSchema = z.object({
  at: z.string(),
  doing: z.array(doingSchema),
  next: z.array(nextSchema),
  halted: z.array(z.object({ thread_id: z.string(), title: z.string().nullable() })),
});

export type OttoNowData = z.infer<typeof ottoNowSchema>;
export type OttoDoing = z.infer<typeof doingSchema>;

/** What a mission's step is, as a verb (the runtime's `action_type`). */
const MISSION_VERB: Record<string, string> = {
  agent_reassess: 'Reprend la mission',
  follow_up: 'Prépare une relance',
  draft_email: 'Rédige un mail',
  wait_for_reply: 'Regarde si on a répondu',
  check_human_signal: 'Regarde si on a répondu',
  escalate_human: 'Prépare une question pour toi',
};

const WORKSHOP_VERB: Record<string, string> = {
  form: 'Remplit un formulaire',
  code: 'Travaille sur du code',
  files: 'Prépare des fichiers',
};

/** « Tri de la boîte mail (classifieur) » → « Tri de la boîte mail »: the loop, without its machinery. */
export function loopLabel(name: string | null): string {
  return (name ?? 'Une boucle').replace(/\s*\([^)]*\)\s*$/u, '').trim() || 'Une boucle';
}

export interface DoingLine {
  key: string;
  kind: OttoDoing['kind'];
  /** « Reprend la mission », « Remplit un formulaire », « Tri de la boîte mail ». */
  verb: string;
  /** What it is about: the client, else the mission's title; null for a loop. */
  about: string | null;
  /** The mission's own title, under the line. */
  detail: string | null;
  threadId: string | null;
  since: string | null;
}

export function doingLine(d: OttoDoing, index: number): DoingLine {
  if (d.kind === 'loop') {
    return { key: `loop-${d.action ?? index}`, kind: 'loop', verb: loopLabel(d.title), about: null, detail: null, threadId: null, since: d.since };
  }
  const verb =
    d.kind === 'workshop'
      ? d.control === 'human'
        ? 'Tu as la main sur l’atelier'
        : (WORKSHOP_VERB[d.action ?? ''] ?? 'Travaille dans l’atelier')
      : (MISSION_VERB[d.action ?? ''] ?? 'Avance la mission');
  return {
    key: `${d.kind}-${d.thread_id ?? index}-${d.action ?? ''}`,
    kind: d.kind,
    verb,
    about: d.client ?? d.title,
    detail: d.client ? d.title : null,
    threadId: d.thread_id,
    since: d.since,
  };
}

/** « à 14 h », « demain à 8 h », « jeu. à 8 h », in the company's zone. */
export function wakeText(iso: string, now: Date, timeZone: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  const day = (d: Date) => new Intl.DateTimeFormat('fr-CA', { timeZone }).format(d);
  const hour = new Intl.DateTimeFormat('fr-FR', { timeZone, hour: 'numeric', minute: '2-digit' })
    .format(at)
    .replace(':', ' h ')
    .replace(/ h 00$/u, ' h');
  const tomorrow = new Date(now.getTime() + 86_400_000);
  if (day(at) === day(now)) return `à ${hour}`;
  if (day(at) === day(tomorrow)) return `demain à ${hour}`;
  const weekday = new Intl.DateTimeFormat('fr-FR', { timeZone, weekday: 'short', day: 'numeric' }).format(at);
  return `${weekday} à ${hour}`;
}

/** « lundi 12 octobre, 9 h », in the company's zone: the date of a planned pass, in full. */
export function wakeLongText(iso: string, timeZone: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  const day = new Intl.DateTimeFormat('fr-FR', { timeZone, weekday: 'long', day: 'numeric', month: 'long' }).format(at);
  const hour = new Intl.DateTimeFormat('fr-FR', { timeZone, hour: 'numeric', minute: '2-digit' })
    .format(at)
    .replace(':', ' h ')
    .replace(/ h 00$/u, ' h');
  return `${day}, ${hour}`;
}

export type OttoNowState =
  | { kind: 'doing'; headline: DoingLine; more: number }
  | { kind: 'halted'; title: string; threadId: string }
  | { kind: 'paused'; next: string | null };

/** The pill's state: doing something (first first), else stuck somewhere, else paused until his next wake-up. */
export function ottoNowState(data: OttoNowData, now: Date, timeZone: string): OttoNowState {
  if (data.doing.length > 0) return { kind: 'doing', headline: doingLine(data.doing[0], 0), more: data.doing.length - 1 };
  const halt = data.halted[0];
  if (halt) return { kind: 'halted', title: halt.title ?? 'une mission', threadId: halt.thread_id };
  const next = data.next[0];
  return { kind: 'paused', next: next ? wakeText(next.at, now, timeZone) : null };
}
